import React from 'react';
import { WebUiButton } from 'src/modules/web-ui/components/button';

import { postWebUiApi } from '../../../api';
import CSystemFilesListTable from './c-system-files-list-table';
import type { FilesGetResponse } from './c-system-settings-types';
import { humanByteSize, arrayBufferToBase64 } from './c-system-settings-utils';

type Props = {
  readonly: boolean;
  data: FilesGetResponse;
  webexport: boolean;
  onWebexportChange: (v: boolean) => void;
  onSetFiles: () => void;
  onReload: () => Promise<void>;
  onFlashOk: (msg: string) => void;
  onFlashError: (msg: string) => void;
};

function sortFileList(
  files: Array<{ name: string; size: number }>,
): Array<{ name: string; size: number }> {
  const tqf = files.find((f) => f.name === 'files.tar');
  const rest = files.filter((f) => f.name !== 'files.tar');
  rest.sort((a, b) => a.name.localeCompare(b.name));
  return tqf ? [tqf, ...rest] : rest;
}

export default function CSystemFilesPanel({
  readonly,
  data,
  webexport,
  onWebexportChange,
  onSetFiles,
  onReload,
  onFlashOk,
  onFlashError,
}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const run = React.useCallback(
    async (fn: () => Promise<void>, ok: string) => {
      try {
        await fn();
        onFlashOk(ok);
        await onReload();
      } catch (err) {
        onFlashError((err as Error).message);
      }
    },
    [onFlashOk, onFlashError, onReload],
  );

  const applyFile = (name: string) =>
    run(
      () =>
        postWebUiApi({
          service: 'system',
          access: 'set',
          command: 'files apply',
          parameters: { file: name },
        }),
      'File apply requested.',
    );

  const deleteFile = (name: string) =>
    run(
      () =>
        postWebUiApi({
          service: 'system',
          access: 'remove',
          command: 'files delete',
          parameters: { file: name },
        }),
      'File deleted.',
    );

  const clearAll = () =>
    run(
      () =>
        postWebUiApi({
          service: 'system',
          access: 'remove',
          command: 'files delete',
        }),
      'All files cleared.',
    );

  const downloadFile = (name: string) => {
    postWebUiApi<{ content: string; name: string }>({
      service: 'system',
      access: 'get',
      command: 'files content',
      parameters: { file: name },
    })
      .then((res) => {
        const binary = atob(res.content);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err: Error) => onFlashError(err.message));
  };

  const uploadSelected = () => {
    const list = inputRef.current?.files;
    if (!list?.length) {
      onFlashError('No file selected.');
      return;
    }
    const fileArr = Array.from(list);
    fileArr
      .reduce(
        (chain, file) =>
          chain.then(async () => {
            const buf = await file.arrayBuffer();
            const b64 = arrayBufferToBase64(buf);
            await postWebUiApi<FilesGetResponse>({
              service: 'system',
              access: 'add',
              command: 'files from file',
              parameters: { file: file.name, content: b64 },
            });
            onFlashOk(`File '${file.name}' uploaded.`);
            await onReload();
          }),
        Promise.resolve(),
      )
      .catch((err: Error) => onFlashError(err.message))
      .finally(() => {
        if (inputRef.current) inputRef.current.value = '';
      });
  };

  const list = sortFileList(data.files ?? []);
  const admin = !readonly;

  return (
    <div className="mt-4 flex flex-col gap-2">
      <h4 className="font-bold">Files Settings</h4>
      <table className="webui-table w-full">
        <thead>
          <tr className="webui-table-row-head">
            <th>Name</th>
            <th>Setting</th>
          </tr>
        </thead>
        <tbody>
          <tr className="webui-table-row-content">
            <td>Remaining free space</td>
            <td>{humanByteSize(data.spacefree ?? 0)}</td>
          </tr>
          <tr className="webui-table-row-content">
            <td>Access files via URL</td>
            <td>
              <input
                type="checkbox"
                disabled={readonly}
                checked={webexport}
                onChange={(e) => onWebexportChange(e.target.checked)}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div>
        <WebUiButton disabled={readonly} onClick={onSetFiles}>
          Set Files
        </WebUiButton>
      </div>

      <h4 className="mt-4 font-bold">Files Upload</h4>
      <div className="flex flex-row gap-2">
        <input
          className="webui-input w-full flex-1"
          ref={inputRef}
          type="file"
          multiple
          disabled={readonly}
        />
        <WebUiButton disabled={readonly} onClick={uploadSelected}>
          Upload File
        </WebUiButton>
      </div>

      <h4 className="mt-4 font-bold">Files List</h4>
      {list.length ? (
        <>
          <CSystemFilesListTable
            list={list}
            webexport={data.webexport}
            admin={admin}
            onApply={(name) => {
              applyFile(name).catch(() => {});
            }}
            onDownload={downloadFile}
            onDelete={(name) => {
              deleteFile(name).catch(() => {});
            }}
          />
          <div>
            <WebUiButton
              disabled={readonly}
              onClick={() => {
                clearAll().catch(() => {});
              }}
            >
              Clear All Files
            </WebUiButton>
          </div>
        </>
      ) : (
        <p className="mt-2 text-xs text-neutral-500">No files.</p>
      )}
    </div>
  );
}
